// Modules
import { RRule, RRuleSet } from "rrule";

/**
 * Frequencies mapping.
 */
const FREQUENCIES = {
    daily: RRule.DAILY,
    weekly: RRule.WEEKLY,
    monthly: RRule.MONTHLY
};

/**
 * Weekdays mapping.
 */
const WEEKDAYS = {
    MO: RRule.MO,
    TU: RRule.TU,
    WE: RRule.WE,
    TH: RRule.TH,
    FR: RRule.FR,
    SA: RRule.SA,
    SU: RRule.SU
};

/**
 * Creates an RRule object for a specific rule.
 *
 * @param {Object} rrule the rule data
 * @returns the RRule object
 */
function createRule(rrule) {
    // NOTE: RRule has an issue around timezones and dayligth saving time changes
    // So the workaround was to set it as UTC (as recommended)
    // and on the dates process apply the timzone offset
    const dtstart = rrule?.dtstart && new Date(rrule?.dtstart);
    const until = rrule?.until && new Date(rrule?.until);

    return new RRule(
        {
            ...rrule,
            freq: FREQUENCIES[rrule?.freq],
            byweekday: rrule?.byweekday && rrule?.byweekday.map(bw => WEEKDAYS[bw]),
            dtstart: new Date(
                Date.UTC(
                    dtstart.getFullYear(),
                    dtstart.getMonth(),
                    dtstart.getDate(),
                    dtstart.getHours(),
                    dtstart.getMinutes(),
                    dtstart.getSeconds()
                )
            ),

            until:
                until &&
                new Date(
                    Date.UTC(
                        until.getFullYear(),
                        until.getMonth(),
                        until.getDate(),
                        until.getHours(),
                        until.getMinutes(),
                        until.getSeconds()
                    )
                )
        },
        true
    );
}

/**
 * Gets a list of event dates for a given event.
 *
 * @param {Object} params the event data, start from and to interval dates
 * @returns {Array} list of dates of the recurrent event
 */
export function getEventDates({ event, startFrom = undefined, startTo = undefined }) {
    if (!event?.rrule) {
        if (startFrom === undefined && startTo === undefined) {
            return event ? [event?.start] : [];
        } else if (startFrom !== undefined && startTo !== undefined) {
            return event && event.start >= startFrom && event.start <= startTo ? [event?.start] : [];
        }
    }

    let rruleSet = new RRuleSet(true);

    // Add a rrule to rruleSet
    rruleSet.rrule(createRule(event.rrule));

    // Add a exclusion rrule to rruleSet
    event.exrule && rruleSet.exrule(createRule(event.exrule));

    // Add the exclusion dates to rruleSet
    if (event.exdate && event.exdate.length > 0) {
        rruleSet._exdate = [];
        event.exdate.forEach(date => {
            rruleSet.exdate(new Date(date));
        });
    }

    if (startFrom && startTo) {
        const dates = rruleSet.between(startFrom, startTo, true).map(date => {
            return new Date(
                Date.UTC(
                    date.getUTCFullYear(),
                    date.getUTCMonth(),
                    date.getUTCDate(),
                    date.getUTCHours(),
                    date.getUTCMinutes() + date.getTimezoneOffset(),
                    date.getUTCSeconds()
                )
            );
        });

        return dates?.filter(date => {
            return !rruleSet._exdate.some(exdate => {
                return date.getTime() === exdate.getTime();
            });
        });
    }

    return rruleSet.all();
}
